import { useEffect, useMemo, useState } from "react";
import Tbody from "./Tbody";
import Thead from "./Thead";
import NoDataFound from "../../common/NodataFound";
import { classNames } from "../../../helpers/classNames";
import Pagination from "../../common/Pagination";
import { handleOnDragEnd } from "../../../helpers";
import { DragDropContext } from "@hello-pangea/dnd";
import TableSkeleton from "../../skeleton/TableSkeleton";
import { isEqual } from "../../../util/Flag";

const Table = ({
  tableImage,
  tableData = [],
  tableHeadData = [],
  containerClasses,
  tableClasses,
  isLoder = true,
  onPageChange = () => {},
  pageCount,
  currentPage,
  list,
  setSelectedItems = () => {},
  selectedItems = [],
  isOrderingDisabled = true,
  handleOrdering = () => {},
  setOriginalData = () => {},
  selectable = false,
  originalData = [],
  tableActionDropdownContainer,
  isFetching = false,
}) => {
  const tableHeader = useMemo(() => {
    if (!selectable) return tableHeadData;

    const alreadyHasCheckbox = tableHeadData.some(
      (th) => th.type === "checkbox"
    );
    if (alreadyHasCheckbox) return tableHeadData;

    return [
      {
        _id: "checkbox",
        type: "checkbox",
        name: "",
        align: "left",
        isSort: false,
        isFilter: false,
        thClasses: "w-[1%] !bg-black/5",
        onCheckboxChange: () => heandleCheckboxSelection({ id: "all" }),
      },
      ...tableHeadData,
    ];
  }, [selectable, tableHeadData]);

  const tableRowData = useMemo(() => {
    return tableData.map((item) => {
      let rowData = [...item.rowData];

      if (selectable && !rowData.some((td) => td.type === "checkbox")) {
        rowData.unshift({
          _id: "checkbox",
          type: "checkbox",
          align: "left",
          data: "X",
          tdClasses: "w-[1%]",
          itemDetails: item,
          checked: selectedItems.includes(item._id),
          onCheckboxChange: () => heandleCheckboxSelection({ id: item._id }),
        });
      } else if (selectable) {
        rowData = rowData.map((td) =>
          td.type === "checkbox"
            ? {
                ...td,
                checked: selectedItems.includes(item._id),
                onCheckboxChange: () =>
                  heandleCheckboxSelection({ id: item._id }),
              }
            : td
        );
      }

      return { ...item, rowData };
    });
  }, [tableData, selectable, selectedItems]);

  const onDragEnd = (result) => {
    handleOnDragEnd(result, originalData, setOriginalData, (val) => {
      handleOrdering(val, originalData);
    });
  };

  const heandleCheckboxSelection = ({ id }) => {
    if (id === "all") {
      const areAllChecked = tableData.every((item) =>
        selectedItems.includes(item._id)
      );

      const updatedSelection = areAllChecked
        ? []
        : tableData.map((item) => item._id);
      setSelectedItems(updatedSelection);
    } else {
      const isChecked = selectedItems.includes(id);
      const updatedSelection = isChecked
        ? selectedItems.filter((itemId) => itemId !== id)
        : [...selectedItems, id];

      setSelectedItems(updatedSelection);
    }
  };

  return (
    <div className="grid">
      <div
        className={classNames(
          "relative w-full overflow-auto rounded-md scroll-smooth scrollbar ",
          pageCount > 1 ? "" : "pb-8",
          containerClasses
        )}
      >
        <DragDropContext onDragEnd={onDragEnd}>
          <table
            className={classNames(
              "w-full table-auto rounded-md bg-white",
              tableClasses
            )}
          >
            <Thead
              tableHeadData={tableHeader}
              checked={
                tableRowData?.length > 0
                  ? tableRowData?.every((item) =>
                      item?.rowData?.every(
                        (row) => row?.type !== "checkbox" || row?.checked
                      )
                    )
                  : false
              }
            />
            {!isLoder && (
              <Tbody
                tableData={tableRowData}
                list={list}
                isOrdering={isOrderingDisabled}
                tableActionDropdownContainer={tableActionDropdownContainer}
              />
            )}
          </table>
        </DragDropContext>
        {isFetching && !isLoder && (
          <div
            className={classNames(
              "absolute right-0 flex items-center gap-2 text-slate-500 font-medium text-sm",
              pageCount > 1 ? "bottom-9" : "bottom-0"
            )}
          >
            <i className="fa-duotone fa-solid fa-spinner-third animate-spin"></i>
            Fetching...
          </div>
        )}
        {isLoder && (
          <div className="w-full">
            <TableSkeleton tableImage={tableImage} />
          </div>
        )}

        {!isLoder && tableData?.length === 0 && (
          <div className="rounded-b-xl bg-white py-5">
            <NoDataFound />
          </div>
        )}
      </div>
      {pageCount > 1 && (
        <Pagination
          paginationClasses={"mt-5 !justify-end"}
          onPageChange={({ selected }) => {
            onPageChange(selected + 1);
          }}
          pageCount={pageCount}
          currentPage={currentPage - 1}
        />
      )}
    </div>
  );
};

export default Table;
